import { Link } from "@tanstack/react-router";
import type { RoomSnapshot, SourceSnapshot } from "@tuit/shared-types";
import styles from "../../styles/components/AdminPage.module.css";
import { ROOM_IDS, relativeTime } from "./utils";

export type InitResult = { created: number; updated: number; existing: number } | null;

interface Props {
  roomId: string;
  onRoomIdChange: (id: string) => void;
  sourceIdInput: string;
  onSourceIdInputChange: (v: string) => void;
  roomSnapshot: RoomSnapshot | null;
  sourceSnapshot: SourceSnapshot | null;
  loadingRoom: boolean;
  loadingRoomConfig: boolean;
  loadingSource: boolean;
  loadingSourceConfig: boolean;
  clearingSource: boolean;
  initializingRooms: boolean;
  initializingSources: boolean;
  initRoomsResult: InitResult;
  initSourcesResult: InitResult;
  onInitRooms: () => void;
  onInitSources: () => void;
  onLoadRoomConfig: () => void;
  onLoadRoom: () => void;
  onLoadSourceConfig: () => void;
  onLoadSource: () => void;
  onClearSource: () => void;
}

export function OpsPanel({
  roomId,
  onRoomIdChange,
  sourceIdInput,
  onSourceIdInputChange,
  roomSnapshot,
  sourceSnapshot,
  loadingRoom,
  loadingRoomConfig,
  loadingSource,
  loadingSourceConfig,
  clearingSource,
  initializingRooms,
  initializingSources,
  initRoomsResult,
  initSourcesResult,
  onInitRooms,
  onInitSources,
  onLoadRoomConfig,
  onLoadRoom,
  onLoadSourceConfig,
  onLoadSource,
  onClearSource,
}: Props) {
  const sources = roomSnapshot?.sourceSnapshots ?? [];

  return (
    <div className={styles.ops}>
      <div className={styles.opsRow}>
        <span className={styles.opsLabel}>Initialize</span>
        <button type="button" className={styles.opBtn} onClick={onInitRooms} disabled={initializingRooms}>
          {initializingRooms ? "…" : "Initialize Rooms"}
        </button>
        {initRoomsResult && (
          <span className={styles.opsResult}>
            {initRoomsResult.created} created, {initRoomsResult.updated} updated, {initRoomsResult.existing} exist
          </span>
        )}
        <button type="button" className={styles.opBtn} onClick={onInitSources} disabled={initializingSources}>
          {initializingSources ? "…" : "Initialize Sources"}
        </button>
        {initSourcesResult && (
          <span className={styles.opsResult}>
            {initSourcesResult.created} created, {initSourcesResult.updated} updated, {initSourcesResult.existing} exist
          </span>
        )}
      </div>

      <div className={styles.opsRow}>
        <span className={styles.opsLabel}>Room</span>
        <select
          className={styles.opsSelect}
          value={roomId}
          aria-label="Room ID"
          onChange={(e) => onRoomIdChange(e.target.value)}
        >
          {ROOM_IDS.map((id) => (
            <option key={id} value={id}>{id}</option>
          ))}
        </select>
        <button type="button" className={styles.opBtn} onClick={onLoadRoomConfig} disabled={loadingRoomConfig}>
          {loadingRoomConfig ? "Loading…" : "Load Config"}
        </button>
        <button type="button" className={styles.opBtn} onClick={onLoadRoom} disabled={loadingRoom}>
          {loadingRoom ? "Loading…" : "Load Snapshot"}
        </button>
        {roomSnapshot && (
          <span className={styles.opsResult}>
            {sources.length} sources · generated {relativeTime(roomSnapshot.generatedAt)}
          </span>
        )}
      </div>

      <div className={styles.opsRow}>
        <span className={styles.opsLabel}>Source</span>
        <input
          className={styles.opsInput}
          type="text"
          placeholder="source id"
          value={sourceIdInput}
          onChange={(e) => onSourceIdInputChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onLoadSource()}
        />
        <button
          type="button"
          className={styles.opBtn}
          onClick={onLoadSourceConfig}
          disabled={loadingSourceConfig || !sourceIdInput.trim()}
        >
          {loadingSourceConfig ? "Loading…" : "Load Config"}
        </button>
        {sourceIdInput.trim() && (
          <Link
            to="/admin/source/$sourceId/editor"
            params={{ sourceId: sourceIdInput.trim() }}
            className={styles.opBtn}
          >
            Edit
          </Link>
        )}
        <button
          type="button"
          className={styles.opBtn}
          onClick={onLoadSource}
          disabled={loadingSource || !sourceIdInput.trim()}
        >
          {loadingSource ? "Loading…" : "Load Snapshot"}
        </button>
        <button
          type="button"
          className={styles.opBtn}
          onClick={onClearSource}
          disabled={clearingSource || !sourceIdInput.trim()}
        >
          {clearingSource ? "Clearing…" : "Clear snapshot"}
        </button>
        {sourceSnapshot && (
          <span className={sourceSnapshot.fetchStatus === "error" ? styles.opsResultError : styles.opsResult}>
            {sourceSnapshot.fetchStatus === "ok"
              ? `${sourceSnapshot.items.length} items · fetched ${relativeTime(sourceSnapshot.fetchedAt)}`
              : `${sourceSnapshot.fetchStatus}: ${sourceSnapshot.errorMessage}`}
          </span>
        )}
      </div>
    </div>
  );
}
